/**
 * Widget Config Fetcher
 *
 * Fetches widget runtime config from the CDN blob snapshot with in-memory caching.
 * Cache TTL: 120 seconds (config blobs have a 60s CDN TTL; we add 60s in-process buffer).
 */

import type { WidgetRuntimeConfig } from "@ethos/shared-types";

interface CacheEntry {
  config: WidgetRuntimeConfig;
  fetchedAt: number;
}

const CONFIG_CACHE_TTL_MS = 120_000;
const cache = new Map<string, CacheEntry>();

/**
 * Fetch widget runtime config.
 *
 * Tries the API path first (/o/{orgId}/w/{widgetId}/config) which works both
 * locally (webapp backend) and in production (routed through CDN to origin).
 * Falls back to the CDN blob snapshot path (/o/{orgId}/w/{widgetId}/config.json)
 * if the API path fails and a separate cdnBase is provided.
 */
export async function fetchWidgetConfig(
  apiBase: string,
  orgId: string,
  widgetId: string,
  cdnBase?: string,
): Promise<WidgetRuntimeConfig | null> {
  const cacheKey = `${orgId}:${widgetId}`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CONFIG_CACHE_TTL_MS) {
    return cached.config;
  }

  // Try API path first (works for local dev and production).
  const apiUrl = `${apiBase}/o/${orgId}/w/${widgetId}/config`;
  let config = await tryFetch(apiUrl);

  // Fall back to CDN blob snapshot if API fails and cdnBase differs.
  if (!config && cdnBase && cdnBase !== apiBase) {
    const cdnUrl = `${cdnBase}/o/${orgId}/w/${widgetId}/config.json`;
    config = await tryFetch(cdnUrl);
  }

  if (config) {
    cache.set(cacheKey, { config, fetchedAt: Date.now() });
  }
  return config;
}

async function tryFetch(url: string): Promise<WidgetRuntimeConfig | null> {
  let response: Response;
  try {
    response = await fetch(url, { method: "GET", credentials: "omit" });
  } catch {
    return null;
  }
  if (!response.ok) return null;
  try {
    return (await response.json()) as WidgetRuntimeConfig;
  } catch {
    return null;
  }
}
